import React from 'react';
import { motion } from 'framer-motion';
import { Leaf, Video } from 'lucide-react';

export const Focus = () => {
  return (
    <section
      id="focus"
      className="py-20 md:py-32 bg-[#FDFCF8]"
      data-testid="focus-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#1A2D23] mb-4">
            What We Focus On
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-[#5C6B63] max-w-3xl mx-auto">
            Two connected pillars driving environmental impact
          </p>
        </motion.div>

        <div className="space-y-20">
          {/* Documentary & Storytelling - FIRST */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="space-y-8">
              <div className="flex items-center justify-start gap-4">
                <a
                  href="https://www.youtube.com/watch?v=m18WvP6CyIc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-4 bg-[#2E5C44] text-white rounded-2xl hover:bg-[#234734] transition-all duration-300 hover:scale-105 flex-shrink-0"
                >
                  <Video className="w-12 h-12" />
                </a>
                <h3 className="text-3xl md:text-4xl font-semibold text-[#1A2D23] text-left">
                  Documentary & Storytelling
                </h3>
              </div>
              <p className="text-lg leading-relaxed text-[#5C6B63] text-left">
                We tell the human stories behind conservation work. Our films and photography capture the dedication, challenges, and hope of people working to protect nature, making environmental action visible and inspiring others to join.
              </p>

              {/* 3 Images Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="rounded-2xl overflow-hidden shadow-lg"
                  style={{ lineHeight: 0 }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-creator-909/artifacts/zxed069n_Captura%20de%20ecra%CC%83%202026-01-27%2C%20a%CC%80s%2020.32.54.png"
                    alt="Azores volcanic landscape"
                    className="w-full h-80 object-cover"
                    style={{ display: 'block' }}
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="rounded-2xl overflow-hidden shadow-lg"
                  style={{ lineHeight: 0 }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/64b2vnwk_Captura%20de%20ecra%CC%83%202026-01-27%2C%20a%CC%80s%2020.31.22.png"
                    alt="Filmmaker with professional camera"
                    className="w-full h-80 object-cover"
                    style={{ display: 'block' }}
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="rounded-2xl overflow-hidden shadow-lg"
                  style={{ lineHeight: 0 }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/nx1zel1f_openart-image_mPLNrW3Y_1771935216290_raw.jpg"
                    alt="Filmmaker at sunset"
                    className="w-full h-80 object-cover"
                    style={{ display: 'block' }}
                  />
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Environmental Education - SECOND */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="space-y-8">
              <div className="flex items-center justify-start gap-4">
                <div className="p-4 bg-[#2E5C44] text-white rounded-2xl flex-shrink-0">
                  <Leaf className="w-12 h-12" />
                </div>
                <h3 className="text-3xl md:text-4xl font-semibold text-[#1A2D23] text-left">
                  Environmental Education
                </h3>
              </div>
              <p className="text-lg leading-relaxed text-[#5C6B63] text-left">
                We bring young people together across Europe to learn directly from nature and each other. Through Erasmus+ programmes and hands on experiences, we create spaces where environmental education becomes personal, meaningful, and transformative.
              </p>

              {/* 3 Images Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="rounded-2xl overflow-hidden shadow-lg"
                  style={{ lineHeight: 0 }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-creator-909/artifacts/4vbte23b_erasmus.webp.avif"
                    alt="Erasmus+ youth programme"
                    className="w-full h-80 object-cover"
                    style={{ display: 'block' }}
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="rounded-2xl overflow-hidden shadow-lg"
                  style={{ lineHeight: 0 }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-creator-909/artifacts/elbiaxnx_openart-image_uLyZqmml_1769440190977_raw.jpg"
                    alt="Students in Azores"
                    className="w-full h-80 object-cover"
                    style={{ display: 'block' }}
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="rounded-2xl overflow-hidden shadow-lg"
                  style={{ lineHeight: 0 }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-creator-909/artifacts/jewksvbl_Captura%20de%20ecra%CC%83%202026-01-27%2C%20a%CC%80s%2020.31.41.png"
                    alt="Hands-on environmental learning"
                    className="w-full h-80 object-cover"
                    style={{ display: 'block' }}
                  />
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mt-16"
        >
          <p className="text-2xl md:text-3xl font-medium text-[#2E5C44] italic">
            Education leads. Storytelling amplifies. People make it happen.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Focus;